import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, Modal, Input } from '@/components/ui';
import OrgPicker, { PartnerNoOrgWarning } from '@/components/affiliate/OrgPicker';
import {
  affiliateApi,
  type AffiliatePartner,
  type UpdatePartnerPatch,
} from '@/api/affiliate';
import { listPartners as listWaPartners } from '@/api/partners';

interface EditPartnerModalProps {
  open: boolean;
  partner: AffiliatePartner;
  onClose: () => void;
}

export default function EditPartnerModal({ open, partner, onClose }: EditPartnerModalProps) {
  const qc = useQueryClient();
  const [name, setName] = useState(partner.name);
  const [email, setEmail] = useState(partner.contact_email ?? '');
  const [phone, setPhone] = useState(partner.contact_phone ?? '');
  const [extPartnerId, setExtPartnerId] = useState(partner.ext_partner_id ?? '');
  const [linkedOrgId, setLinkedOrgId] = useState<string | null>(partner.linked_org_id);

  useEffect(() => {
    if (!open) return;
    setName(partner.name);
    setEmail(partner.contact_email ?? '');
    setPhone(partner.contact_phone ?? '');
    setExtPartnerId(partner.ext_partner_id ?? '');
    setLinkedOrgId(partner.linked_org_id);
  }, [open, partner]);

  const { data: waPartners } = useQuery({
    queryKey: ['wa-partners-for-affiliate'],
    queryFn: () => listWaPartners(),
    enabled: open,
  });

  const updateMut = useMutation({
    mutationFn: () => {
      const patch: UpdatePartnerPatch = {
        name,
        contact_email: email || null,
        contact_phone: phone || null,
        ext_partner_id: extPartnerId || null,
        linked_org_id: linkedOrgId,
      };
      return affiliateApi.partners.update(partner.id, patch);
    },
    onSuccess: () => {
      toast.success('Partner updated');
      qc.invalidateQueries({ queryKey: ['affiliate-partner', partner.id] });
      qc.invalidateQueries({ queryKey: ['affiliate-partners'] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message ?? 'Failed to update partner'),
  });

  return (
    <Modal isOpen={open} onClose={onClose} title="Edit affiliate partner">
      <div className="space-y-4">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Contact email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
          <Input label="Contact phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>

        <div>
          <label className="block text-sm font-medium text-ink-secondary mb-1.5">
            Link to WhatsApp partner (optional)
          </label>
          <select
            value={extPartnerId}
            onChange={(e) => setExtPartnerId(e.target.value)}
            className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm"
          >
            <option value="">— none —</option>
            {(waPartners ?? []).map((w) => (
              <option key={w.id} value={w.id}>
                {w.display_name} ({w.slug})
              </option>
            ))}
          </select>
        </div>

        <OrgPicker
          label="JumpStart organization"
          value={linkedOrgId}
          onChange={setLinkedOrgId}
          hint="The partner's own JumpStart organization. Leave empty only if they haven't registered yet."
        />

        <PartnerNoOrgWarning show={!linkedOrgId} />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!name.trim()) {
                toast.error('Name is required');
                return;
              }
              updateMut.mutate();
            }}
            loading={updateMut.isPending}
          >
            Save changes
          </Button>
        </div>
      </div>
    </Modal>
  );
}
